/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { DataAxisProps } from '../../UNSAFE_Axis/axis.types';
import { ChartItem } from '../../UNSAFE_Chart';
import { BarSeries } from '../../UNSAFE_BarChart/barChart.types';

/**
 * Returns the min and max value among the items rendered in the viewport.
 * @param series The array of chart series.
 * @param viewportMin The current viewport min.
 * @param viewportMax The current viewport max.
 * @returns
 */
export function getDataMinMax<K extends string | number, D extends ChartItem<K>>(
  series: BarSeries<K, D>[],
  xStartIndex: number,
  xEndIndex: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  isStacked: boolean,
  isLog: boolean,
  hiddenSet: Set<string | number>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale'
) {
  let dataMin = Number.MAX_VALUE;
  let dataMax = Number.MIN_VALUE;

  for (let groupIndex = xStartIndex; groupIndex <= xEndIndex; groupIndex++) {
    let posCumulValue = 0;
    let negCumulValue = 0;

    series.forEach((_: BarSeries<K, D>, seriesIndex: number) => {
      const item = getDataItem(seriesIndex, groupIndex);
      const skipData =
        item?.id != null && hiddenSet.has(item.id) && hideAndShowBehavior === 'withRescale';
      if (!item || skipData || (item.value <= 0 && isLog)) return;
      if (isStacked) {
        posCumulValue += item.value > 0 ? item.value : 0;
        negCumulValue += item.value < 0 ? item.value : 0;
        dataMin = Math.min(isLog ? posCumulValue : negCumulValue, dataMin);
        dataMax = Math.max(posCumulValue, dataMax);
      } else {
        dataMin = Math.min(dataMin, item.value);
        dataMax = Math.max(dataMax, item.value);
      }
    });
  }

  return { dataMin, dataMax };
}

/**
 * Returns the dataMin and dataMax, used for axis extend calculations, from the chart data.
 * @param series
 * @param hiddenIds
 * @param xStartIndex
 * @param xEndIndex
 * @param yAxis
 * @returns
 */
export function getDataExtent<K extends string | number, D extends ChartItem<K>>(
  series: BarSeries<K, D>[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  xStartIndex: number,
  xEndIndex: number,
  isStacked: boolean,
  hiddenSet: Set<string | number>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale',
  yAxis?: DataAxisProps
) {
  let dataMin = yAxis?.dataMin;
  let dataMax = yAxis?.dataMax;

  if (dataMin != null && dataMax != null) {
    return { dataMin, dataMax };
  }
  const data = getDataMinMax(
    series,
    xStartIndex,
    xEndIndex,
    getDataItem,
    isStacked,
    yAxis?.scale === 'log',
    hiddenSet,
    hideAndShowBehavior
  );

  if (dataMin == null) {
    dataMin = data.dataMin;
  }

  if (dataMax == null) {
    dataMax = data.dataMax;
  }
  return { dataMax, dataMin };
}
